import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SEO from '../components/SEO';
import { generateJSONLD } from '../utils/seo';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'How do you choose which casinos to review?',
      answer: 'We only review casinos that are licensed by the UK Gambling Commission. We evaluate them based on factors including bonus offers, game selection, payment methods, customer support, user experience, and overall reputation. Our goal is to provide you with information about safe, reputable casinos that offer genuine value.',
    },
    {
      question: 'Are the bonuses you list actually available?',
      answer: 'Yes, we verify all bonuses before listing them. However, casino offers can change frequently, so we recommend checking the casino\'s website directly before signing up. We update our reviews regularly to reflect current offers.',
    },
    {
      question: 'Do you get paid by casinos for reviews?',
      answer: 'Some casinos may pay us a commission if you sign up through our links. However, this never influences our reviews or ratings. We maintain complete editorial independence and only recommend casinos that meet our quality standards, regardless of any commercial relationships.',
    },
    {
      question: 'What is wagering requirement?',
      answer: 'A wagering requirement is the number of times you must bet your bonus amount before you can withdraw any winnings. For example, if you receive a £100 bonus with a 35x wagering requirement, you must wager £3,500 before withdrawing. We always clearly display wagering requirements in our reviews.',
    },
    {
      question: 'How quickly can I withdraw my winnings?',
      answer: 'Withdrawal times vary by casino and payment method. E-wallets typically process within 24 hours, while bank transfers can take 3-5 business days. We include payout time information in each casino review to help you make informed decisions.',
    },
    {
      question: 'Is online gambling safe in the UK?',
      answer: 'Yes, online gambling is legal and regulated in the UK. All casinos we review are licensed by the UK Gambling Commission, which ensures they meet strict standards for fairness, security, and responsible gambling. Always verify a casino\'s license before playing.',
    },
    {
      question: 'Can I play at these casinos from outside the UK?',
      answer: 'Most UK-licensed casinos require you to be physically located in the UK to play. If you\'re traveling or living abroad, check the casino\'s terms and conditions regarding geographic restrictions. Some casinos may allow play from certain other jurisdictions.',
    },
    {
      question: 'What should I do if I have a problem with a casino?',
      answer: 'First, contact the casino\'s customer support directly. If the issue isn\'t resolved, you can file a complaint with the UK Gambling Commission or use the casino\'s alternative dispute resolution (ADR) service. We recommend only playing at casinos with clear complaint procedures.',
    },
  ];

  const schema = generateJSONLD({
    type: 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  });

  return (
    <>
      <SEO
        title="Frequently Asked Questions - GameObsidian"
        description="Find answers to common questions about UK casino offers, bonuses, wagering requirements, and more. Get the information you need to make informed decisions."
        url="/faq"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schema }}
      />

      <div className="min-h-screen pt-20 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h1>
            <p className="text-xl text-dark-muted">
              Everything you need to know about UK casino offers and our reviews
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="glass-effect rounded-xl overflow-hidden transition-all"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full p-6 flex items-center justify-between text-left hover:bg-dark-card/50 transition-colors"
                >
                  <span className="font-semibold text-lg pr-4">{faq.question}</span>
                  {openIndex === index ? (
                    <FaChevronUp className="text-accent-primary flex-shrink-0" />
                  ) : (
                    <FaChevronDown className="text-accent-primary flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-6 text-dark-muted leading-relaxed animate-fade-in">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 glass-effect rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-dark-muted mb-6">
              Can't find what you're looking for? Feel free to reach out to us.
            </p>
            <a
              href="mailto:contact@gameobsidian.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary hover:opacity-90 rounded-lg font-semibold transition-opacity"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;

